import {
  Escalation,
  LocationAlarmIncidentDTO,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';

/** The keys an alarm group is matched against when scoping incidents to it. */
export interface EscalationScope {
  alarmGroupId?: string;
  sourceGroupId?: string;
  locationAlarmTypeId?: string;
  /**
   * Only consulted when the scope carries no source group -- the keys decide
   * it otherwise, for every product. Reachable from `isEscalationInScope`
   * alone: `isEscalationOwnedBy` either delegates into the keyed path or
   * matches the anchor, and neither reads this.
   */
  product?: UnifiedMonitoringProduct;
}

/** The scoping keys, on either shape that carries them: a unified group's
 * `Escalation` or a location alarm's `LocationAlarmIncidentDTO`. */
export type ScopedEscalation = Partial<
  Pick<
    Escalation,
    'sourceGroupId' | 'locationAlarmTypeId' | 'initiatingLocationAlarmId'
  >
>;

/**
 * Whether an escalation belongs to the alarm group being viewed.
 *
 * `locationIncidents` carries every active incident at the location -- the
 * backend does not scope it -- so the match is made here.
 *
 * On v3 the scoping keys decide it, the same way for every product: one active
 * incident per (source group, alarm type), and the anchor is not usable because
 * dedup means a second alarm escalating into an existing scope reuses that
 * incident and inherits an anchor pointing at a sibling. An incident with no
 * type key is not type-scoped, so a group match is all that can be asked of it.
 *
 * v2 has no source group, so the keys are unavailable and product still
 * matters: remote guarding holds one incident per location, so everything there
 * is in scope, while enterprise can hold several and only the anchor says which
 * belongs to this alarm.
 *
 * See `isEscalationOwnedBy` for ownership, which adds a v2/scan anchor fallback.
 */
/**
 * Without scoping keys the incident's anchor alarm is all there is to match
 * on. Shared with `isEscalationOwnedBy`, so v2-enterprise scope and v2/scan
 * ownership are visibly the same rule rather than two copies that drift.
 */
export function matchesAnchorAlarm(
  escalation: ScopedEscalation,
  scope: EscalationScope
): boolean {
  return (
    !!scope.alarmGroupId &&
    escalation.initiatingLocationAlarmId === scope.alarmGroupId
  );
}

export function isEscalationInScope(
  escalation: ScopedEscalation,
  scope: EscalationScope
): boolean {
  if (!scope.sourceGroupId) {
    return scope.product === UnifiedMonitoringProduct.ENTERPRISE
      ? matchesAnchorAlarm(escalation, scope)
      : true;
  }
  if (escalation.sourceGroupId !== scope.sourceGroupId) {
    return false;
  }
  return (
    !escalation.locationAlarmTypeId ||
    escalation.locationAlarmTypeId === scope.locationAlarmTypeId
  );
}

/**
 * Open first -- that is the only one an operator can act on -- then the most
 * recently started, so a fully resolved collection still renders its latest
 * state rather than its first.
 */
function pickLatest<T>(
  items: readonly T[],
  isOpen: (item: T) => boolean,
  startedAt: (item: T) => string | undefined
): T | undefined {
  if (items.length === 0) {
    return undefined;
  }
  const open = items.filter(isOpen);
  const candidates = open.length > 0 ? open : items;

  return candidates.reduce((latest, item) => {
    // Invalid/absent stamps sort oldest rather than poisoning the comparison
    // with NaN, which would make the result depend on array order.
    const a = Date.parse(startedAt(item) ?? '');
    const b = Date.parse(startedAt(latest) ?? '');
    return (Number.isNaN(a) ? -Infinity : a) > (Number.isNaN(b) ? -Infinity : b)
      ? item
      : latest;
  });
}

/**
 * The incident to display/act on, from an alarm's `locationIncidents`.
 *
 * Callers must narrow to the alarm's scope first (see `isEscalationInScope`):
 * this only chooses between incidents already known to apply, and any of them
 * may be anchored to a sibling alarm in the same group.
 */
export function selectActiveIncident(
  incidents?: readonly LocationAlarmIncidentDTO[]
): LocationAlarmIncidentDTO | undefined {
  return pickLatest(
    incidents ?? [],
    (i) => !i.incidentEndTimeUtc,
    (i) => i.incidentStartTimeUtc
  );
}

/**
 * The escalation to display/act on, from a unified group's `escalations`, which
 * arrives carrying the whole location's. Narrow to scope first.
 */
export function selectActiveEscalation(
  escalations?: readonly Escalation[]
): Escalation | undefined {
  return pickLatest(
    escalations ?? [],
    (e) => !e.incidentEndTimeUtc,
    (e) => e.incidentStartTimeUtc
  );
}
